import { Button, Icons, useToggle } from '@commenda/kakashi';
import {
  LegacyDocumentType,
  TaskParty,
  TaskStatus,
} from '@prisma/client';
import { ServiceRequestTaskLabel } from '../../../common/labels';
import { CorporationUploadFileModal } from '../../../common/modals/CorporationUploadFileModal';
import { ServiceRequestTaskIconConfig } from '../../../common/taskIconConfig';
import { useServiceRequestFile } from '../../../hook/useServiceRequestFile';
import { ServiceRequestTaskCard } from '../../../layout/components/taskCard/ServiceRequestTaskCard';
import { ServiceRequestTaskCardConfig, TCommonTaskProp } from '../../../types';
import { updateTaskStatus, viewUploadedFile } from '../../../utils';

export const BankStatements = ({
  task,
  context,
  isTimelineView,
  displayNumber,
}: TCommonTaskProp) => {
  const { role, serviceRequest, mutate } = context;

  const [file, setFile] = useServiceRequestFile(
    serviceRequest,
    LegacyDocumentType.BankStatements
  );

  const corporationBankStatements =
    serviceRequest?.corporation?.corporationDocuments?.find(
      (eachFile) => eachFile?.documentType === LegacyDocumentType.BankStatements
    );

  const recentlyUploadedBankStatementsFile =
    corporationBankStatements?.files.sort((a, b) => a.id - b.id)[0];

  const uploadModalVisibility = useToggle();

  const config: ServiceRequestTaskCardConfig = {
    getLeftIcon: (status) => ServiceRequestTaskIconConfig[status],
    getStateConfig: (state) => {
      const stateMap = {
        [TaskParty.CLIENT]: {
          [TaskStatus.COMPLETED]: {
            actions: (
              <Button
                size="sm"
                variant="secondary"
                onClick={() =>
                  viewUploadedFile(recentlyUploadedBankStatementsFile.id)
                }
              >
                View <Icons.ArrowRight className="size-4" />
              </Button>
            ),
            label: (
              <ServiceRequestTaskLabel
                label="Foreign Entity's bank statement uploaded"
                status={task.status}
                timestamp={task.updatedAt}
              />
            ),
          },
          [TaskStatus.TO_DO]: {
            actions: (
              <Button
                size="sm"
                onClick={uploadModalVisibility.toggle}
                variant="primary"
              >
                Upload <Icons.Upload01 className="size-4" />
              </Button>
            ),
            label: (
              <ServiceRequestTaskLabel label="Upload Foreign Entity's bank statement" />
            ),
          },
          [TaskStatus.BACKLOG]: {
            actions: (
              <Button
                disabled
                size="sm"
                onClick={uploadModalVisibility.toggle}
                variant="primary"
              >
                Upload <Icons.Upload01 className="size-4" />
              </Button>
            ),
            label: (
              <ServiceRequestTaskLabel label="Upload Foreign Entity's bank statement" />
            ),
          },
        },
        [TaskParty.AGENT]: {
          [TaskStatus.COMPLETED]: {
            actions: (
              <Button
                size="sm"
                onClick={uploadModalVisibility.toggle}
                variant="primary"
              >
                Re-upload <Icons.Upload01 className="size-4" />
              </Button>
            ),
            label: (
              <ServiceRequestTaskLabel
                label="Client uploads Foreign Entity's bank statement"
                status={task.status}
                timestamp={task.updatedAt}
              />
            ),
          },
          [TaskStatus.TO_DO]: {
            actions: null,
            label: (
              <ServiceRequestTaskLabel label="Client uploads Foreign Entity's bank statement" />
            ),
          },
          [TaskStatus.BACKLOG]: {
            actions: null,
            label: (
              <ServiceRequestTaskLabel label="Client uploads Foreign Entity's bank statement" />
            ),
          },
        },
      };

      return (
        stateMap[state.role]?.[state.status] ?? {
          label: task.title,
          actions: null,
        }
      );
    },
  };

  return (
    <div>
      <ServiceRequestTaskCard
        task={task}
        state={{ role, status: task.status }}
        config={config}
        context={context}
        isTimelineView={isTimelineView}
        displayNumber={displayNumber}
      />

      <CorporationUploadFileModal
        serviceRequest={serviceRequest}
        serviceRequestTaskId={task.id}
        isOpen={uploadModalVisibility.visible}
        setIsOpen={uploadModalVisibility.hide}
        file={file}
        setFile={setFile}
        heading={`Upload ${serviceRequest?.corporation?.companyLegalName}'s bank statement`}
        subheading="Make sure the credited amount is included."
        documentType={LegacyDocumentType.BankStatements}
        onUploadSuccess={() =>
          updateTaskStatus({
            task,
            status: TaskStatus.COMPLETED,
            mutate,
          })
        }
      />
    </div>
  );
};
